import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { dashboardViewQuery } from '../../api/command-dashboard-api';
import EmptyStateMessage from '../../shared/empty-state-message';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../shared/unsaved-changes-dialog';
import { Messages } from '../../utils/message-constants';
import { useUserSettings } from '../../utils/use-user-settings';
import CommandDashboardGrid from './command-dashboard-grid';
import DashboardViewMenuButton from './dashboard-view-menu-button';
import FlaggedSectionHeader from './flagged-section-header';
import TileLibrary, { AddTilesButton } from './tile-library';
import { useCommandDashboardActions } from './use-command-dashboard-actions';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DashboardCustomizeOutlined from '@mui/icons-material/DashboardCustomizeOutlined';

export default function CommandDashboardPage() {
  const theme = useTheme();
  const { userName, marketingOverview } = useUserSettings();
  const {
    isLayoutLoaded,
    isDirty,
    tileRows,
    availableTiles,
    addTile,
    placeTile,
    moveTile,
    removeTile,
  } = useCommandDashboardActions();
  const { data: dashboardView } = useQuery(dashboardViewQuery());
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { showDialog, handleConfirm, handleCancel, isCancelAction } =
    useUnsavedChanges(isDirty);

  const heightOfMarketingOverview = marketingOverview ? 48 : 0;
  const tileLibrary = (
    <TileLibrary
      availableTiles={availableTiles}
      onAdd={addTile}
      open={libraryOpen}
      onOpen={() => setLibraryOpen(true)}
      onClose={() => setLibraryOpen(false)}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(100vh - 260px - ${heightOfMarketingOverview}px)`,
      }}
    >
      <FlaggedSectionHeader
        flagTestId="dashboard-title-flag"
        title={`Hi ${userName},`}
        titleVariant="h5"
        helperText={Messages.CommandDashboard.GreetingSubtitle}
        helperTextSx={{ mt: 0.5 }}
      />
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{
          bgcolor: theme.palette.semantic.surface.bg1,
          borderRadius: '8px',
          // Same light/dark split as the tiles below it: a shadow in light mode, a border once
          // dark mode makes that shadow unreadable against the page background.
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'none'
              : '0px 1px 4px 0px rgba(0, 0, 0, 0.12)',
          border:
            theme.palette.mode === 'dark'
              ? `0.5px solid ${theme.palette.semantic.outline.light}`
              : 'none',
          p: 2,
          my: 2,
        }}
      >
        <DashboardViewMenuButton dashboardView={dashboardView} />
        {tileLibrary}
      </Stack>
      {/* The grid is held back rather than rendered empty: its tiles come from the saved view
          this query returns, so drawing it early would flash the empty state (or the wrong
          tiles) at anyone who has a view saved. */}
      {!isLayoutLoaded ? (
        <Stack
          role="status"
          aria-live="polite"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ flexGrow: 1, py: 8 }}
        >
          <CircularProgress />
          <Typography
            variant="body2"
            sx={{ color: theme.palette.semantic.text.support }}
          >
            {Messages.CommandDashboard.LoadingDashboardView}
          </Typography>
        </Stack>
      ) : (
        <CommandDashboardGrid
          tileRows={tileRows}
          onRemove={removeTile}
          onDropTile={placeTile}
          onMoveTile={moveTile}
          emptyState={
            <EmptyStateMessage
              icon={
                <DashboardCustomizeOutlined
                  fontSize="large"
                  sx={{ color: theme.palette.semantic.text.title }}
                />
              }
              title="Welcome to gPanel Command Dashboard!"
              titleSx={{
                color: theme.palette.semantic.text.title,
                whiteSpace: 'nowrap',
              }}
              spacing={1.5}
              message={Messages.CommandDashboard.EmptyStateMessage}
              // The message is transparent to the pointer so drags reach the grid
              // behind it, which the button has to opt back out of.
              action={
                <Box sx={{ pointerEvents: 'auto' }}>
                  <AddTilesButton
                    disabled={availableTiles.length === 0}
                    onClick={() => setLibraryOpen(true)}
                  />
                </Box>
              }
            />
          }
        />
      )}
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        pageMode="edit"
        entityName="Dashboard"
        open={showDialog}
        handleConfirm={handleConfirm}
        handleClose={handleCancel}
      />
    </Box>
  );
}
